import { Link } from "react-router-dom";
import { Routes } from "@/constants/enums";
import { appPath } from "@/constants/paths";
import { useObsWizard } from "@/features/obs/hooks/obs-wizard-context";
import {
  isStepReachable,
  STEPS,
  stepIndex,
} from "@/features/obs/lib/wizard-steps";
import { cn } from "@/lib/utils";

const WIZARD_PATH = appPath(Routes.OBS_REPORTS, "new");

/**
 * The step strip — a progress indicator that is also the navigation.
 *
 * Each step is a real link, so an operator who wants to re-read what they
 * confirmed two steps ago goes straight there instead of pressing «السابق»
 * twice. A step the draft cannot yet support is rendered as plain text rather
 * than a dead link: offering it and then bouncing the click is worse than not
 * offering it.
 */
export function ObsWizardSteps() {
  const { step, gates, published } = useObsWizard();
  const current = stepIndex(step);

  return (
    <ol className="flex flex-wrap gap-2 text-xs">
      {STEPS.map((s, i) => {
        const active = i === current;
        // Past publication the wizard is a receipt, not a form — going back to
        // change a title would edit a report that is already on the site, which
        // is what the report's own card is for.
        const reachable = !published && !active && isStepReachable(s.id, gates);
        const className = cn(
          "rounded-full border px-3 py-1 transition-colors",
          active
            ? "border-primary bg-primary/10 font-semibold text-primary"
            : i < current
              ? "border-emerald-500/40 text-emerald-600"
              : "border-border text-muted-foreground",
          reachable && "hover:bg-muted",
        );
        const label = `${i + 1}. ${s.label}`;

        return (
          <li key={s.id}>
            {reachable ? (
              <Link
                to={`${WIZARD_PATH}/${s.id}`}
                className={className}
                aria-current={undefined}
              >
                {label}
              </Link>
            ) : (
              <span className={className} aria-current={active ? "step" : undefined}>
                {label}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
